import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Lenis from "lenis";
import { SiteContext } from "../context/SiteContext";
import { computePricing, formatPrice, http } from "../lib/core";
import { initPixel } from "../lib/pixel";
import { useNow } from "../hooks/useCountdown";
import { TopBar, Header } from "../components/event/TopBar";
import { Hero, Ticker } from "../components/event/Hero";
import { MobileBar } from "../components/event/Chrome";

const BelowFold = lazy(() => import("../components/event/BelowFold"));

export default function Landing() {
  const [settings, setSettings] = useState(null);
  const now = useNow(15000);
  const lenisRef = useRef(null);

  useEffect(() => {
    initPixel();
    http.get("/settings").then((r) => setSettings(r.data ?? null)).catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); lenisRef.current = null; };
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -56 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const value = useMemo(
    () => ({
      settings,
      pricing: settings ? computePricing(settings, now) : null,
      fmt: settings ? formatPrice : () => "₹…",
      scrollTo,
    }),
    [settings, now, scrollTo]
  );

  return (
    <SiteContext.Provider value={value}>
      <div data-testid="landing-page" className="min-h-screen overflow-x-clip bg-[#0A0A0A] text-[#F5F0E6]">
        <TopBar />
        <div className="relative">
          <div className="absolute inset-x-0 top-0 z-10"><Header /></div>
          <Hero />
        </div>
        <Ticker />
        <Suspense fallback={<div className="min-h-screen" />}>
          <BelowFold />
        </Suspense>
        <MobileBar />
      </div>
    </SiteContext.Provider>
  );
}
